import dagre from 'dagre';
import { Node, Edge, Position } from 'reactflow';
import type { OrgNode } from '@/types';

/**
 * Convert hierarchical OrgNode tree to React Flow nodes and edges
 */
export function convertOrgTreeToFlow(
  rootNode: OrgNode,
  collapsedNodes: string[] = []
): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // Recursively traverse tree and create nodes/edges
  function traverse(node: OrgNode, depth: number = 0) {
    const isCollapsed = collapsedNodes.includes(node.pid);
    
    // Add node
    nodes.push({
      id: node.pid,
      type: 'orgNode',
      data: {
        ...node,
        isCollapsed,
        depth,
      },
      position: { x: 0, y: 0 }, // Will be set by layout
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
    });

    // Add edges and traverse children if not collapsed
    if (!isCollapsed && node.directReports.length > 0) {
      node.directReports.forEach((child) => {
        edges.push({
          id: `${node.pid}-${child.pid}`,
          source: node.pid,
          target: child.pid,
          type: 'smoothstep',
          animated: false,
          style: {
            stroke: 'hsl(var(--border))',
            strokeWidth: 2,
          },
        });
        
        traverse(child, depth + 1);
      });
    }
  }

  traverse(rootNode);

  return { nodes, edges };
}

/**
 * Apply Dagre layout algorithm to position nodes
 */
export function getLayoutedElements(
  nodes: Node[],
  edges: Edge[],
  direction: 'TB' | 'LR' = 'TB'
): { nodes: Node[]; edges: Edge[] } {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  const nodeWidth = 250;
  const nodeHeight = 100;
  const rankSep = 80; // Vertical spacing between levels
  const nodeSep = 60; // Horizontal spacing between siblings

  dagreGraph.setGraph({
    rankdir: direction,
    ranksep: rankSep,
    nodesep: nodeSep,
    edgesep: 40,
    marginx: 20,
    marginy: 20,
  });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
}

/**
 * Filter visible nodes based on collapsed state
 */
export function filterVisibleFlowNodes(
  allNodes: Node[],
  allEdges: Edge[],
  collapsedNodes: string[]
): { nodes: Node[]; edges: Edge[] } {
  const visibleNodeIds = new Set<string>();
  const nodeLookup = new Map(allNodes.map(n => [n.id, n]));

  // Find all nodes that should be visible
  function markVisible(nodeId: string) {
    if (visibleNodeIds.has(nodeId)) return;
    
    visibleNodeIds.add(nodeId);
    
    if (!collapsedNodes.includes(nodeId)) {
      // Find children
      allEdges
        .filter(edge => edge.source === nodeId)
        .forEach(edge => markVisible(edge.target));
    }
  }

  // Start from root (assume first node is root)
  if (allNodes.length > 0) {
    markVisible(allNodes[0].id);
  }

  const visibleNodes = allNodes.filter(node => visibleNodeIds.has(node.id));
  const visibleEdges = allEdges.filter(
    edge => visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target)
  );

  return { nodes: visibleNodes, edges: visibleEdges };
}
